import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Box,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Spinner,
  Switch,
} from "@salestrak/ui";
import { PageHeader, PageHeaderSubtitle, PageHeaderTitle } from "#app/components/page-header";
import { useUserSettings } from "#app/lib/hooks/use-user-settings";
import { SettingsGroup, SettingsGroupRow } from "#app/components/settings/settings-group";
import {
  THEME_MODE_OPTIONS,
  ThemeAccentSelect,
  ThemeResetButton,
  ThemeSurfaceSelect,
  ThemeContrastSelect,
  useIsAppearanceCustomized,
  useThemeModePreference,
  type ThemeMode,
} from "#app/components/theme/theme-appearance-controls";

// Every control in the right-hand column shares one width, so the Selects line
// up with the sliders instead of each sizing to its own content.
const CONTROL_WIDTH = 180;
import { DEFAULT_ROUTE_OPTIONS } from "#app/lib/default-route";
// The toast anchors live with the viewport that honors them, so the Select
// here and the provider can never drift apart.
import {
  DEFAULT_TOAST_POSITION,
  TOAST_POSITION_OPTIONS,
} from "#app/components/app-toast-provider";
import { at } from "#app/lib/utils/at";

const FONT_SIZE_OPTIONS = [{ label: "Default", value: "default" }];

// Base UI's Select hands back `string | null`; narrow before it reaches the
// mode setter rather than casting.
function isThemeMode(value: string | null): value is ThemeMode {
  return THEME_MODE_OPTIONS.some((o) => o.value === value);
}

// Stored preferences travel as untyped strings (null while unloaded,
// arbitrary if stale) — resolve to a known option value or the fallback.
function resolveValue(
  options: readonly { value: string; label: string }[],
  val: string | null | undefined,
  fallback: string,
): string {
  return options.some((o) => o.value === val) ? (val as string) : fallback;
}

// Appearance mode as a Select, matching every other control on this page.
// The three appearance controls below are real rows here rather than the
// sub-menu's compact stack, so a popover would only hide duplicates of them.
function ThemeModeSelect() {
  const { mode, setMode } = useThemeModePreference();

  return (
    <Box width={CONTROL_WIDTH}>
      <Select
        items={THEME_MODE_OPTIONS.map((o) => ({
          label: o.label,
          value: o.value,
        }))}
        value={resolveValue(
          THEME_MODE_OPTIONS.map((o) => ({ label: o.label, value: o.value })),
          mode,
          "system",
        )}
        onValueChange={(value) => {
          if (isThemeMode(value)) {
            setMode(value);
          }
        }}
      >
        <SelectTrigger size="sm" aria-label="Theme">
          <SelectValue />
        </SelectTrigger>
        <SelectPopup>
          {THEME_MODE_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectPopup>
      </Select>
    </Box>
  );
}

function PreferencesPage() {
  // No preferences row yet (new user) simply means every field reads null and
  // each Select resolves to its own fallback — there is no need to fabricate a
  // whole default row to stand in for one.
  const { preferences, isLoading, error, updatePreference } = useUserSettings();
  const isAppearanceCustomized = useIsAppearanceCustomized();

  if (isLoading && !preferences) {
    return (
      <Box fill="flex" direction="column" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (error && !preferences) {
    return (
      <Box fill="flex" direction="column" align="center" justify="center">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Unable to load preferences</EmptyTitle>
            <EmptyDescription>{error.message}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </Box>
    );
  }

  return (
    <Box fill="flex" overflow="auto">
      <PageHeader centered>
        <PageHeaderTitle>Preferences</PageHeaderTitle>
        <PageHeaderSubtitle>Customize your workspace display and behavior.</PageHeaderSubtitle>
      </PageHeader>
      <Box direction="column" align="center" paddingY="md" paddingX="md">
        <Box direction="column" gap="xl" fill="width" maxWidth={800}>
          <SettingsGroup label="General">
            <SettingsGroupRow
              label="Default view"
              subtitle="Which view is opened when SalesTrak is opened"
            >
              <Box width={CONTROL_WIDTH}>
                <Select
                  items={DEFAULT_ROUTE_OPTIONS.map((o) => ({ ...o }))}
                  value={resolveValue(
                    DEFAULT_ROUTE_OPTIONS,
                    preferences?.defaultRoute,
                    DEFAULT_ROUTE_OPTIONS[0].value,
                  )}
                  onValueChange={(value) => {
                    if (typeof value === "string") {
                      updatePreference("defaultRoute", value);
                    }
                  }}
                >
                  <SelectTrigger size="sm" aria-label="Default view">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectPopup>
                    {DEFAULT_ROUTE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectPopup>
                </Select>
              </Box>
            </SettingsGroupRow>

            <SettingsGroupRow
              label="Display full names"
              subtitle="Display full names of members instead of usernames"
            >
              <Switch
                checked={preferences?.displayFullNames ?? false}
                onCheckedChange={(v) =>
                  updatePreference("displayFullNames", v)
                }
              />
            </SettingsGroupRow>
          </SettingsGroup>

          <SettingsGroup label="Display">
            <SettingsGroupRow label="Theme" subtitle="Select your color theme">
              <ThemeModeSelect />
            </SettingsGroupRow>

            <SettingsGroupRow
              label="Background"
              subtitle="Make every surface lighter or darker"
            >
              <ThemeSurfaceSelect width={CONTROL_WIDTH} />
            </SettingsGroupRow>

            <SettingsGroupRow
              label="Text contrast"
              subtitle="How strongly text reads against the background"
            >
              <ThemeContrastSelect width={CONTROL_WIDTH} />
            </SettingsGroupRow>

            <SettingsGroupRow
              label="Accent"
              subtitle="The color used for primary actions and selection"
            >
              <ThemeAccentSelect width={CONTROL_WIDTH} />
            </SettingsGroupRow>

            <SettingsGroupRow
              label="Font size"
              subtitle="Adjust the size of text across the entire app"
            >
              <Box width={CONTROL_WIDTH}>
                <Select
                  items={FONT_SIZE_OPTIONS.map((o) => ({ ...o }))}
                  value={resolveValue(
                    FONT_SIZE_OPTIONS,
                    preferences?.fontSize,
                    at(FONT_SIZE_OPTIONS, 0, "font size option").value,
                  )}
                  onValueChange={(value) => {
                    if (typeof value === "string") {
                      updatePreference("fontSize", value);
                    }
                  }}
                >
                  <SelectTrigger size="sm" aria-label="Font size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectPopup>
                    {FONT_SIZE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectPopup>
                </Select>
              </Box>
            </SettingsGroupRow>

            <SettingsGroupRow
              label="Toast position"
              subtitle="Where notifications appear on screen"
            >
              <Box width={CONTROL_WIDTH}>
                <Select
                  items={TOAST_POSITION_OPTIONS.map((o) => ({ ...o }))}
                  value={resolveValue(
                    TOAST_POSITION_OPTIONS,
                    preferences?.toastPosition,
                    DEFAULT_TOAST_POSITION,
                  )}
                  onValueChange={(value) => {
                    if (typeof value === "string") {
                      updatePreference("toastPosition", value);
                    }
                  }}
                >
                  <SelectTrigger size="sm" aria-label="Toast position">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectPopup>
                    {TOAST_POSITION_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectPopup>
                </Select>
              </Box>
            </SettingsGroupRow>
          </SettingsGroup>

          {isAppearanceCustomized ? (
            <Box direction="row" justify="end">
              <ThemeResetButton />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/preferences")({
  component: PreferencesPage,
});
